import type { QueryResult, SearchItineraryStore } from '$houdini';
import type { ItineraryResultsStore, ItineraryResultsStoreContent } from '$lib/components/itinerary/types';
import type { Route, TrafficMentionWithGeometry } from '$lib/components/map/types';
import type { SearchParams } from '$lib/services/urls/searchParams';
import type { AugmentedHoudiniStore } from '$lib/utils/augmentedHoudiniStore';
import type { LayoutLoadEvent as CardsLayoutLoadEvent, LayoutServerLoadEvent } from './$types';
import type { LayoutLoadEvent } from './(details)/$types';
import type { PageLoadEvent } from './results/[...addresses]/$types';
import type { WaypointsInfo } from './types';
import { browser } from '$app/environment';
import { getWaypointCoordinates } from '$lib/components/search/itinerary/ItineraryStore';
import { resetAdServedIds } from '$lib/services/advertising/orange/adserving';
import { getPartnersDeviceType } from '$lib/utils/getPartnersDeviceType';
import { filterUndefined } from '$lib/utils/predicates';
import mapboxPolyline from '@mapbox/polyline';
import { get } from 'svelte/store';

export interface FetchSearchItineraryStoreArgs {
	waypointsInfo: WaypointsInfo;
	searchItineraryStore: AugmentedHoudiniStore<SearchItineraryStore>;
	searchParams: SearchParams;
	onSuccess?: () => void;
}

export interface LoadSearchItineraryArgs {
	waypointsInfo: WaypointsInfo;
	searchParams: SearchParams;
	searchItineraryStore?: AugmentedHoudiniStore<SearchItineraryStore>; // Optional since function creates its own store
}

type LoadEvent = PageLoadEvent | LayoutLoadEvent | CardsLayoutLoadEvent | LayoutServerLoadEvent;

export async function fetchSearchItineraryStore(
	event: LoadEvent,
	{ waypointsInfo, searchItineraryStore, searchParams, onSuccess }: FetchSearchItineraryStoreArgs,
): Promise<QueryResult> {
	const { waypoints: urlWaypoints, departure, arrival } = waypointsInfo;
	const coordinates = await Promise.all(urlWaypoints.map(getWaypointCoordinates));

	if (browser) {
		resetAdServedIds();
	}

	const { data } = await searchItineraryStore.fetch({
		event,
		variables: {
			input: {
				coordinates: coordinates.filter(filterUndefined),
				departureName: departure.name,
				arrivalName: arrival.name,
				carId: searchParams.car?.id,
				constraint: searchParams.tripConstraint,
				distanceSystem: searchParams.distanceSystem,
				mode: searchParams.travelMode,
				device: getPartnersDeviceType(),
				// TODO: replace with values from user input in trip options
				energyCost: searchParams.energyPrice,
				currency: searchParams.currency,
				rechargeTreshold: searchParams.rechargeTreshold,
				withCaravan: searchParams.withCaravan,
				traffic: searchParams.traffic,
			},
		},
	});

	onSuccess?.();

	return data;
}

interface GetItineraryResultsStoreArgs {
	searchItineraryStore: AugmentedHoudiniStore<SearchItineraryStore>;
}

export function getItineraryResultsStore({ searchItineraryStore }: GetItineraryResultsStoreArgs) {
	return searchItineraryStore.derive(($store) => {
		const baseData: ItineraryResultsStoreContent = {
			fetching: $store.fetching,
			hasError: !!$store.errors,
			partners: {
				cards: [],
			},
			routes: [],
			adsTextLines: [],
			closingsMessage: { text: '' },
			waypoints: [],
			arrivalZoi: undefined,
			departureAddress: undefined,
			arrivalAddress: undefined,
			departureCityPath: null,
			arrivalCityPath: null,
			isSuccessResult: $store.data?.searchItinerary?.__typename === 'SearchItinerarySuccessResult',
		};

		if (
			!$store.data?.searchItinerary
			|| $store.data.searchItinerary.__typename === 'SearchItineraryNotFoundResult'
		) {
			if (!$store.data?.searchItinerary) {
				// NOTE: Set data to fetching state until itinerary search succeeds or fails
				baseData.fetching = true;
			}

			return baseData;
		}

		const itiResultsStoreContent: ItineraryResultsStoreContent = {
			...baseData,
			waypoints: $store.data.searchItinerary.waypoints,
			routes: $store.data.searchItinerary.routes.map((route: Route, index: number) => ({
				...route,
				geometry: mapboxPolyline.toGeoJSON(route.geometry),
				encodedGeometry: route.geometry,
				trafficMentions: (route?.trafficMentions?.filter(Boolean).map((trafficMention: NonNullable<Route['trafficMentions']>[number]) => trafficMention && ({
					...trafficMention,
					geometry: { line: mapboxPolyline.toGeoJSON(trafficMention.geometry), index },
					encodedGeometry: trafficMention.geometry,
					color: trafficMention.color,
				})) ?? []) as TrafficMentionWithGeometry[],
				index,
			})),
			arrivalZoi: $store.data.searchItinerary.arrivalZoi ?? undefined,
			departureAddress: $store.data.searchItinerary.departureAddress,
			arrivalAddress: $store.data.searchItinerary.arrivalAddress,
			adsTextLines: $store.data.searchItinerary.adsTextLines,
			closingsMessage: $store.data.searchItinerary.closingsMessage,
			departureCityPath: $store.data.searchItinerary.departureCityPath,
			arrivalCityPath: $store.data.searchItinerary.arrivalCityPath,
		};

		return itiResultsStoreContent;
	});
}

export function itineraryResultsStoreUtils(itineraryResultsStore: ItineraryResultsStore) {
	const itineraryResultsData = get(itineraryResultsStore);

	return {
		getDepartureWaypoint() {
			return itineraryResultsData.waypoints[0];
		},
		getArrivalWaypoint() {
			return itineraryResultsData.waypoints.at(-1);
		},
	};
}
